import type { AnswerSource, TransportChunk } from "../types.ts";

export interface QonnexAgentStreamResult {
  handled: boolean;
  done: boolean;
  chunks: TransportChunk[];
}

/** Normalize the existing QonnexAgent SSE protocol. Progress is kept as
 * transient UI state; tool internals never become assistant messages. */
export function normalizeQonnexAgentEvent(
  event: string,
  payload: Record<string, unknown>,
): QonnexAgentStreamResult {
  if (event === "done" || event === "end") {
    const answer = firstText(payload, ["answer", "reply", "content", "text"]);
    return {
      handled: true,
      done: true,
      chunks: answer ? [{ type: "message", text: answer }] : [],
    };
  }

  if (event === "sources") {
    if (Array.isArray(payload.sources) || Array.isArray(payload.citations)) {
      return { handled: true, done: false, chunks: [] };
    }
    const sources = searchSources(payload.message);
    return {
      handled: true,
      done: false,
      chunks: sources.length ? [{ type: "sources", sources }] : [],
    };
  }

  if (event === "progress") {
    return {
      handled: true,
      done: false,
      chunks: [
        {
          type: "progress",
          stage: firstText(payload, ["stage"]) || "progress",
          status: firstText(payload, ["status"]) || "started",
        },
      ],
    };
  }

  if (
    event === "tool_call" ||
    event === "tool_result" ||
    event === "search_preview" ||
    event === "artifact"
  ) {
    return { handled: true, done: false, chunks: [] };
  }

  return { handled: false, done: false, chunks: [] };
}

function searchSources(value: unknown): AnswerSource[] {
  const parsed = recordFrom(value);
  const items = Array.isArray(parsed?.items) ? parsed.items : [];
  const sources: AnswerSource[] = [];

  for (const item of items) {
    if (!isRecord(item)) continue;
    const label = firstText(item, ["title", "label", "source", "domain", "url"]);
    if (!label) continue;

    const source: AnswerSource = { label };
    const at = firstText(item, ["published_time", "publishedTime", "at", "date"]);
    const scope = firstText(item, ["source", "domain", "scope"]);
    if (at) source.at = at;
    if (scope && scope !== label) source.scope = scope;
    sources.push(source);
  }

  return sources;
}

function recordFrom(value: unknown): Record<string, unknown> | null {
  if (isRecord(value)) return value;
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function firstText(source: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
